import { useState, useEffect } from 'react'
import { Toggle } from '../components/Toggle'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { TrashIcon } from '../components/icons/TrashIcon'
import { PencilIcon } from '../components/icons/PencilIcon'
import { LightningIcon } from '../components/icons/LightningIcon'
import { WarningIcon } from '../components/icons/WarningIcon'
import { FEATURES } from '../lib/features'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../lib/api'
import type {
  Skill,
  SkillRef,
  SkillCreate,
  SkillUpdate,
  SkillListResponse,
  SkillType,
} from '../lib/types'
import {
  SkillForm,
  SkillFieldErrors,
  getSkillFormErrors,
  buildSkillFieldErrors,
  focusFirstSkillFormError,
} from '../components/SkillForm'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import { Spinner } from '../components/Spinner'
import { Card } from '../components/Card'
import { useEntityEditorRegistration } from '../hooks/useEntityEditorRegistration'

/* ─── Badge helpers ─── */

function TypeBadge({ type }: { type: SkillType }) {
  const { t } = useTranslation()
  if (type === 'Execute') {
    return (
      <span className="badge-execute-wrap">
        <span className="execute-warn">
          <WarningIcon size={14} />
          <span className="execute-warn-tip">{t('skillsTab.badge.executeWarning')}</span>
        </span>
        <span className="badge badge-execute">{t('skillsTab.editor.typeExecute')}</span>
      </span>
    )
  }
  return (
    <span className="badge badge-read">
      {t('skillsTab.editor.typeRead')}
    </span>
  )
}

/* ─── Toggle switch ─── */

/* ─── Editor form shape ─── */

interface EditorForm {
  name: string
  type: SkillType
  shortDescription: string
  agentSummary: string
  definition: string
}

const emptyEditorForm: EditorForm = {
  name: '',
  type: 'Read',
  shortDescription: '',
  agentSummary: '',
  definition: '',
}

/* ─── Props ─── */

export interface SkillsTabProps {
  agentId: string
  isReadOnly?: boolean
  localBindings: SkillRef[]
  globalBindings: SkillRef[]
  mambuBindings: SkillRef[]
  onLocalToggle: (skillId: string, isEnabled: boolean) => void
  onGlobalToggle: (skillId: string, isEnabled: boolean) => void
  onMambuToggle: (skillId: string, isEnabled: boolean) => void
  onLocalSkillAdded: (binding: SkillRef) => void
  onLocalSkillRemoved: (skillId: string) => void
  onDraftDirtyChange?: (dirty: boolean) => void
}

/* ─── Main SkillsTab component ─── */

export function SkillsTab({
  agentId,
  isReadOnly = false,
  localBindings,
  globalBindings,
  mambuBindings,
  onLocalToggle,
  onGlobalToggle,
  onMambuToggle,
  onLocalSkillAdded,
  onLocalSkillRemoved,
  onDraftDirtyChange,
}: Readonly<SkillsTabProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()

  /* ── Editor state ── */
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null)
  const [editorMode, setEditorMode] = useState<'idle' | 'create' | 'edit'>('idle')
  const [editorForm, setEditorForm] = useState<EditorForm>(emptyEditorForm)
  const [originalForm, setOriginalForm] = useState<EditorForm | null>(null)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [pendingEntitySwitch, setPendingEntitySwitch] = useState<Skill | 'create' | null>(null)
  const [editorError, setEditorError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<SkillFieldErrors>({})

  const isEditorDirty = originalForm !== null && JSON.stringify(editorForm) !== JSON.stringify(originalForm)

  useEffect(() => {
    onDraftDirtyChange?.(isEditorDirty)
    return () => onDraftDirtyChange?.(false)
  }, [isEditorDirty, onDraftDirtyChange])

  // Entity-editor registry (Skill/Agent) — reuses the existing isEditorDirty
  // snapshot above; page-level Save/Discard wiring (onDraftDirtyChange) is
  // unchanged and stays separate from this registry.
  useEntityEditorRegistration({
    isOpen: () => editorMode !== 'idle',
    isDirty: () => isEditorDirty,
    markClean: () => closeEditor(),
  })

  /* ── Delete error (shown inline above skill list) ── */
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [deleteConfirm, setDeleteConfirm] = useState<Skill | null>(null)

  /* ── Pagination state ── */
  const [globalPagedData, setGlobalPagedData] = useState<{ items: Skill[]; cursor: string | null }>({ items: [], cursor: null })
  const [isLoadingMoreGlobal, setIsLoadingMoreGlobal] = useState(false)

  /* ── Queries ── */
  const localSkillsQuery = useQuery({
    queryKey: ['skills', 'local', agentId],
    queryFn: () => apiFetch<SkillListResponse>(`/api/v1/skills?scope=Local&ownerAgentId=${agentId}`),
  })

  const globalSkillsQuery = useQuery({
    queryKey: ['skills', 'global'],
    queryFn: () => apiFetch<SkillListResponse>('/api/v1/skills?scope=Global'),
    enabled: FEATURES.globalSkillsConfig,
  })

  const mambuSkillsQuery = useQuery({
    queryKey: ['skills', 'mambu', agentId],
    queryFn: () => apiFetch<SkillListResponse>(`/api/v1/skills?scope=Mambu&ownerAgentId=${agentId}`),
  })
  const mambuSkills: Skill[] = mambuSkillsQuery.data?.items ?? []

  useEffect(() => {
    if (globalSkillsQuery.data) {
      setGlobalPagedData({ items: globalSkillsQuery.data.items, cursor: globalSkillsQuery.data.nextCursor })
    }
  }, [globalSkillsQuery.data])

  const handleLoadMoreGlobal = async () => {
    if (!globalPagedData.cursor) return
    setIsLoadingMoreGlobal(true)
    try {
      const url = `/api/v1/skills?scope=Global&cursor=${encodeURIComponent(globalPagedData.cursor)}`
      const result = await apiFetch<SkillListResponse>(url)
      setGlobalPagedData(prev => ({
        items: [...prev.items, ...result.items],
        cursor: result.nextCursor,
      }))
    } catch (err) {
      console.error('Failed to load more global skills', err)
    } finally {
      setIsLoadingMoreGlobal(false)
    }
  }

  /* ── Derived data ── */
  const localSkills: Skill[] = localSkillsQuery.data?.items ?? []
  const globalSkills: Skill[] = globalPagedData.items
  const visibleGlobalSkills = globalSkills.filter(
    (skill) => skill.isEnabled || globalBindings.some((b) => b.skillId === skill.id)
  )

  /* ── Helpers ── */

  function localIsEnabled(skillId: string): boolean {
    return localBindings.find(b => b.skillId === skillId)?.isEnabled ?? true
  }

  function globalIsEnabled(skillId: string): boolean {
    return globalBindings.find(b => b.skillId === skillId)?.isEnabled ?? false
  }

  function mambuIsEnabled(skillId: string): boolean {
    return mambuBindings.find(b => b.skillId === skillId)?.isEnabled ?? true
  }

  function skillGloballyEnabled(skillId: string): boolean {
    return globalSkills.some(skill => skill.id === skillId && skill.isEnabled)
  }

  function openCreate() {
    setSelectedSkillId(null)
    setEditorMode('create')
    setEditorForm(emptyEditorForm)
    setOriginalForm(emptyEditorForm)
    setEditorError(null)
    setFieldErrors({})
  }

  function openEdit(skill: Skill) {
    setSelectedSkillId(skill.id)
    setEditorMode('edit')
    const form: EditorForm = {
      name: skill.name,
      type: isReadOnly ? 'Read' : skill.type,
      shortDescription: skill.shortDescription ?? '',
      agentSummary: skill.agentSummary ?? '',
      definition: skill.definition ?? '',
    }
    setEditorForm(form)
    setOriginalForm(form)
    setEditorError(null)
    setFieldErrors({})
  }

  function closeEditor() {
    setEditorMode('idle')
    setSelectedSkillId(null)
    setOriginalForm(null)
    setShowDiscardDialog(false)
    setEditorError(null)
    setFieldErrors({})
  }

  function requestClose() {
    if (isEditorDirty) {
      setShowDiscardDialog(true)
    } else {
      closeEditor()
    }
  }

  function requestSwitch(target: Skill | 'create') {
    if (isEditorDirty) {
      setPendingEntitySwitch(target)
      setShowDiscardDialog(true)
    } else if (target === 'create') {
      openCreate()
    } else {
      openEdit(target)
    }
  }

  /* ── Mutations ── */

  const createMutation = useMutation({
    mutationFn: (body: SkillCreate) =>
      apiFetch<Skill>('/api/v1/skills', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: (skill) => {
      void queryClient.invalidateQueries({ queryKey: ['skills', 'local', agentId] })
      onLocalSkillAdded({ skillId: skill.id, isEnabled: true })
      closeEditor()
    },
    onError: (err) => {
      setEditorError(err instanceof Error ? err.message : t('skillsTab.errors.failedToSave'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: SkillUpdate }) =>
      apiFetch<Skill>(`/api/v1/skills/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['skills', 'local', agentId] })
      closeEditor()
    },
    onError: (err) => {
      setEditorError(err instanceof Error ? err.message : t('skillsTab.errors.failedToSave'))
    },
  })

  const deleteLocalMutation = useMutation({
    mutationFn: (skillId: string) =>
      apiFetch<void>(`/api/v1/skills/${skillId}`, { method: 'DELETE' }),
    onSuccess: (_, skillId) => {
      void queryClient.invalidateQueries({ queryKey: ['skills', 'local', agentId] })
      onLocalSkillRemoved(skillId)
      closeEditor()
      setDeleteError(null)
      setDeleteConfirm(null)
    },
    onError: (err) => {
      setDeleteError(err instanceof Error ? err.message : t('skillsTab.errors.failedToDelete'))
      setDeleteConfirm(null)
    },
  })

  /* ── Save handler ── */

  function handleEditorSave() {
    const errorKeys = getSkillFormErrors(editorForm)
    if (Object.keys(errorKeys).length > 0) {
      setFieldErrors(buildSkillFieldErrors(errorKeys, t))
      focusFirstSkillFormError(errorKeys, 'skill-edit')
      return
    }
    setFieldErrors({})
    setEditorError(null)

    const trimmedName = editorForm.name.trim()

    if (editorMode === 'create') {
      createMutation.mutate({
        name: trimmedName,
        type: editorForm.type,
        scope: 'Local',
        ownerAgentId: agentId,
        definition: editorForm.definition.trim(),
        shortDescription: editorForm.shortDescription.trim(),
        agentSummary: editorForm.agentSummary.trim(),
      })
    } else if (editorMode === 'edit' && selectedSkillId) {
      updateMutation.mutate({
        id: selectedSkillId,
        body: {
          name: trimmedName,
          type: editorForm.type,
          definition: editorForm.definition.trim(),
          shortDescription: editorForm.shortDescription.trim(),
          agentSummary: editorForm.agentSummary.trim(),
        },
      })
    }
  }

  const isMutating = createMutation.isPending || updateMutation.isPending || deleteLocalMutation.isPending

  /* ── Render ── */

  return (
    <div>
      {/* ── Local Skills ── */}
      <Card title={t('skillsTab.local.title')} subtitle={t('skillsTab.local.subtitle')} noPadding={true} className="editor-split-card">
        <div className="editor-split-body">

          {/* Left: skill list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
            {deleteError && (
              <div
                role="alert"
                style={{
                  marginBottom: 12,
                  padding: '8px 12px',
                  background: 'var(--danger-bg)',
                  border: '1px solid var(--danger)',
                  borderRadius: 6,
                  fontSize: 12,
                  color: 'var(--danger)',
                }}
              >
                {deleteError}
              </div>
            )}
            {localSkillsQuery.isLoading && (
              <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
                <Spinner size={20} />
              </div>
            )}
            {localSkillsQuery.isError && (
              <div style={{ padding: '12px 20px', color: 'var(--danger)', fontSize: 13 }}>
                {t('skillsTab.errors.failedToLoad')}
              </div>
            )}
            {!localSkillsQuery.isLoading && !localSkillsQuery.isError && localSkills.length === 0 && (
              <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                {t('skillsTab.empty.noLocal')}
              </div>
            )}
            {localSkills.map((skill) => {
              const isSelected = selectedSkillId === skill.id && editorMode === 'edit'
              const isEnabled = localIsEnabled(skill.id)
              return (
                <div
                  key={skill.id}
                  className={`skill-row${isSelected ? ' selected' : ''}`}
                  onClick={() => requestSwitch(skill)}
                >
                  <div className="conn-icon icon-skill" aria-hidden="true">
                    <LightningIcon />
                  </div>
                  <div className="skill-info">
                    <div className="skill-name">
                      {skill.name}
                    </div>
                    {skill.shortDescription && (
                      <div className="skill-desc">
                        {skill.shortDescription}
                      </div>
                    )}
                  </div>
                  <TypeBadge type={skill.type} />
                  <Toggle
                    checked={isEnabled}
                    onChange={(v) => {
                      onLocalToggle(skill.id, v)
                    }}
                    label={t('skillsTab.enableLabel', { name: skill.name })}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    type="button"
                    className="btn-text skill-edit-btn"
                    onClick={(e) => { e.stopPropagation(); requestSwitch(skill) }}
                    aria-label={t('globalSkillsTab.editAria', { name: skill.name })}
                  >
                    <PencilIcon size={14} />
                  </button>
                  <button
                    type="button"
                    className="skill-remove-btn"
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(skill) }}
                    aria-label={t('globalSkillsTab.deleteAria', { name: skill.name })}
                  >
                    <TrashIcon size={14} />
                  </button>
                </div>
              )
            })}
          </div>

          {/* Separator */}
          <div style={{ width: 1, background: 'var(--border-light)', margin: '20px 0' }} />

          {/* Right: editor pane */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 24px', overflow: 'hidden' }}>
            {editorMode === 'idle' ? (
              /* Empty state */
              <div
                id="skill-editor-empty"
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 12,
                  color: 'var(--text-tertiary)',
                }}
              >
                <LightningIcon />
                <div style={{ fontSize: 13, textAlign: 'center' }}>
                  {t('skillsTab.empty.selectOrCreate')}
                </div>
                <button
                  type="button"
                  id="add-skill-btn"
                  onClick={() => requestSwitch('create')}
                  className="btn btn-primary"
                >
                  {t('skillsTab.addNew')}
                </button>
              </div>
            ) : (
              /* Editor form */
              <div id="skill-editor" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                {/* Editor header */}
                <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-light)' }}>
                  <h4
                    id="skill-editor-title"
                    style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}
                  >
                    {editorMode === 'create' ? t('skillsTab.editor.newTitle') : t('skillsTab.editor.editTitle')}
                  </h4>
                </div>

                {/* Editor body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                  <SkillForm
                    idPrefix="skill-edit"
                    value={editorForm}
                    errors={fieldErrors}
                    readOnlyAgent={isReadOnly}
                    onChange={(patch) => {
                      setEditorForm(f => ({ ...f, ...patch }))
                      setFieldErrors(prev => {
                        const next = { ...prev }
                        for (const key of Object.keys(patch) as (keyof SkillFieldErrors)[]) delete next[key]
                        return next
                      })
                    }}
                  />

                  {editorError && (
                    <div role="alert" style={{ padding: '8px 12px', background: 'var(--danger-bg)', borderRadius: 6, fontSize: 13, color: 'var(--danger)' }}>
                      {editorError}
                    </div>
                  )}
                </div>

                <div style={{ paddingTop: 16, borderTop: '1px solid var(--border-light)', flexShrink: 0 }}>
                  {/* Editor footer */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                      type="button"
                      id="skill-editor-cancel2"
                      onClick={requestClose}
                      className="btn btn-ghost"
                      style={{ fontSize: 12 }}
                    >
                      {t('skillsTab.editor.cancel')}
                    </button>
                    <button
                      type="button"
                      id="skill-editor-save"
                      onClick={handleEditorSave}
                      disabled={isMutating || Object.keys(fieldErrors).length > 0}
                      className="btn btn-primary"
                      style={{ fontSize: 12, cursor: isMutating ? 'wait' : 'pointer', opacity: (isMutating || Object.keys(fieldErrors).length > 0) ? 0.65 : 1 }}
                    >
                      {isMutating ? t('skillsTab.editor.saving') : t('skillsTab.editor.save')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ── Global Skills ── */}
      {/* Hidden while Global Settings → Skills is preview-only (MS-866 AC1):
          the card has nothing meaningful to show or toggle when global skill
          management isn't a real surface yet. */}
      {FEATURES.globalSkillsConfig && (
      <Card
        title={t('skillsTab.global.title')}
        subtitle={t('skillsTab.global.subtitle')}
        actions={
          <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => navigate('/global-settings/skills')}>
            {t('common.manageInGlobalSettings')}
          </button>
        }
      >
        <div>
          {globalSkillsQuery.isLoading && (
            <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
              <Spinner size={20} />
            </div>
          )}
          {globalSkillsQuery.isError && (
            <div style={{ padding: '12px 0', color: 'var(--danger)', fontSize: 13 }}>
              {t('skillsTab.errors.failedToLoadGlobal')}
            </div>
          )}
          {!globalSkillsQuery.isLoading && !globalSkillsQuery.isError && visibleGlobalSkills.length === 0 && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
              {t('skillsTab.empty.noGlobal')}
            </div>
          )}
          {visibleGlobalSkills.map((skill) => {
            const isEnabled = globalIsEnabled(skill.id)
            const isGloballyEnabled = skillGloballyEnabled(skill.id)
            return (
              <div key={skill.id}>
                <div className="skill-row">
                  <div className="conn-icon icon-skill-global" aria-hidden="true">
                    <LightningIcon />
                  </div>
                  <div className="skill-info">
                    <div className="skill-name">
                      {skill.name}
                    </div>
                    {skill.shortDescription && (
                      <div className="skill-desc">
                        {skill.shortDescription}
                      </div>
                    )}
                  </div>
                  <TypeBadge type={skill.type} />
                  {!isGloballyEnabled && (
                    <span className="execute-warn" style={{ color: 'var(--warning)' }}>
                      <WarningIcon size={14} />
                      <span className="execute-warn-tip">{t('skillsTab.globallyDisabledBound')}</span>
                    </span>
                  )}
                  <Toggle
                    checked={isEnabled}
                    onChange={(v) => onGlobalToggle(skill.id, v)}
                    label={t('skillsTab.enableForSpark', { name: skill.name })}
                    disabled={!isGloballyEnabled}
                  />
                </div>
              </div>
            )
          })}
          {globalPagedData.cursor && (
            <div style={{ paddingTop: 12 }}>
              <button
                type="button"
                onClick={handleLoadMoreGlobal}
                disabled={isLoadingMoreGlobal}
                className="btn btn-secondary"
                style={{ width: '100%', fontSize: 12, opacity: isLoadingMoreGlobal ? 0.6 : 1, cursor: isLoadingMoreGlobal ? 'default' : 'pointer' }}
              >
                {isLoadingMoreGlobal ? <Spinner size={12} /> : t('common.showMore')}
              </button>
            </div>
          )}
        </div>
      </Card>
      )}

      {/* ── Mambu Skills ── */}
      {mambuSkills.length > 0 && (
        <Card
          title={t('skillsTab.mambu.title')}
          subtitle={t('skillsTab.mambu.subtitle')}
        >
          <div>
            {mambuSkills.map((skill) => (
              <div key={skill.id} className="skill-row">
                <div className="conn-icon icon-skill-mambu" aria-hidden="true">
                  <LightningIcon />
                </div>
                <div className="skill-info">
                  <div className="skill-name">{skill.name}</div>
                </div>
                <span className={`badge badge-${skill.type.toLowerCase()}`}>
                  {skill.type === 'Read' ? t('skillsTab.editor.typeRead') : t('skillsTab.editor.typeExecute')}
                </span>
                <Toggle
                  checked={mambuIsEnabled(skill.id)}
                  onChange={(v) => onMambuToggle(skill.id, v)}
                  label={t('skillsTab.enableLabel', { name: skill.name })}
                />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Delete confirmation dialog ── */}
      {deleteConfirm && (
        <ConfirmDialog
          title={t('skillsTab.deleteDialog.title', { name: deleteConfirm.name })}
          message={t('skillsTab.deleteDialog.message')}
          confirmLabel={t('skillsTab.deleteDialog.confirm')}
          danger
          onConfirm={() => deleteLocalMutation.mutate(deleteConfirm.id)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {/* ── Discard unsaved changes dialog ── */}
      {showDiscardDialog && (
        <UnsavedChangesDialog
          onConfirm={() => {
            setShowDiscardDialog(false)
            if (pendingEntitySwitch === 'create') {
              openCreate()
            } else if (pendingEntitySwitch) {
              openEdit(pendingEntitySwitch)
            } else {
              closeEditor()
            }
            setPendingEntitySwitch(null)
          }}
          onCancel={() => {
            setShowDiscardDialog(false)
            setPendingEntitySwitch(null)
          }}
        />
      )}
    </div>
  )
}
